import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFinancialReports } from '@/composables/financial/useFinancialReports';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { toast } from 'sonner';

export function ReportsPage() {
  const queryClient = useQueryClient();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const params = { dateFrom, dateTo };
  const { pnl, pnlLoading, cashFlow, cashFlowLoading, expenseSummary, expenseSummaryLoading, revenueForecast, revenueForecastLoading, aging, agingLoading } = useFinancialReports(params);

  const handleGenerate = () => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select both start and end dates');
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['financial-reports'] });
  };

  return (
    <PageContainer title="Financial Reports" description="Generate and view financial reports">
      <Card className="mb-6">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1"><Label>From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
          <div className="space-y-1"><Label>To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
          <Button onClick={handleGenerate} disabled={!dateFrom || !dateTo}>Generate</Button>
        </CardContent>
      </Card>

      <Tabs defaultValue="pnl">
        <TabsList><TabsTrigger value="pnl">P&L</TabsTrigger><TabsTrigger value="cashflow">Cash Flow</TabsTrigger><TabsTrigger value="expenses">Expense Summary</TabsTrigger><TabsTrigger value="revenue">Revenue Forecast</TabsTrigger><TabsTrigger value="aging">Aging</TabsTrigger></TabsList>
        <TabsContent value="pnl" className="mt-4">
          {pnlLoading ? <LoadingSpinner /> : <Card><CardHeader><CardTitle>Profit & Loss</CardTitle></CardHeader><CardContent>{pnl ? <pre className="text-sm overflow-auto">{JSON.stringify(pnl, null, 2)}</pre> : <p className="text-muted-foreground">Select a date range and click Generate</p>}</CardContent></Card>}
        </TabsContent>
        <TabsContent value="cashflow" className="mt-4">
          {cashFlowLoading ? <LoadingSpinner /> : <Card><CardHeader><CardTitle>Cash Flow</CardTitle></CardHeader><CardContent>{cashFlow ? <pre className="text-sm overflow-auto">{JSON.stringify(cashFlow, null, 2)}</pre> : <p className="text-muted-foreground">Select a date range and click Generate</p>}</CardContent></Card>}
        </TabsContent>
        <TabsContent value="expenses" className="mt-4">
          {expenseSummaryLoading ? <LoadingSpinner /> : <Card><CardHeader><CardTitle>Expense Summary</CardTitle></CardHeader><CardContent>{expenseSummary ? <pre className="text-sm overflow-auto">{JSON.stringify(expenseSummary, null, 2)}</pre> : <p className="text-muted-foreground">Select a date range and click Generate</p>}</CardContent></Card>}
        </TabsContent>
        <TabsContent value="revenue" className="mt-4">
          {revenueForecastLoading ? <LoadingSpinner /> : <Card><CardHeader><CardTitle>Revenue Forecast</CardTitle></CardHeader><CardContent>{revenueForecast ? <pre className="text-sm overflow-auto">{JSON.stringify(revenueForecast, null, 2)}</pre> : <p className="text-muted-foreground">Revenue forecast based on deal pipeline</p>}</CardContent></Card>}
        </TabsContent>
        <TabsContent value="aging" className="mt-4">
          {agingLoading ? <LoadingSpinner /> : <Card><CardHeader><CardTitle>Invoice Aging</CardTitle></CardHeader><CardContent>{aging ? <pre className="text-sm overflow-auto">{JSON.stringify(aging, null, 2)}</pre> : <p className="text-muted-foreground">Invoice aging report</p>}</CardContent></Card>}
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
